import React, { useState } from 'react';
import { 
  MdSmartToy, MdLocalShipping, MdScience, 
  MdSchool, MdLocalGroceryStore, MdLocalCafe,
  MdExpandMore, MdExpandLess, MdCheckCircle,
  MdArrowRight, MdVerified, 
  MdWork,  MdPeople, 
  MdArrowForward, MdPlayCircle, MdStar, MdOutlineAgriculture,
  MdBarChart, MdEco, MdSecurity, MdAccessTime
} from 'react-icons/md';
import { FaSeedling,  FaGlobe, FaLeaf, FaAppleAlt, FaTrophy } from 'react-icons/fa';
import { GiWheat, GiPlantWatering } from 'react-icons/gi';

const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  // Using EXACT same colors from Home page
  const primaryGreen = '#2E7D32';
  const primaryBlue = '#0288D1';
  const primaryOrange = '#F57C00';
  const primaryPurple = '#7B1FA2';
  const primaryRed = '#D32F2F';

  const services = [
    {
      id: 1,
      icon: <MdSmartToy />,
      title: 'Agri Robotics & Automation',
      description: 'Intelligent automation solutions for modern farming operations',
      color: primaryGreen,
      gradient: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
      features: [
        'AI-powered crop monitoring systems',
        'Automated precision planting technology',
        'Smart spraying robots with sensors',
        'Autonomous harvesting machines',
        'Drone-based field surveillance'
      ],
      benefits: [
        '60% reduction in labor costs',
        '40% increase in crop yield',
        '24/7 operational capability',
        'Precision farming accuracy',
        'Reduced resource wastage'
      ],
      keywords: ['agricultural robotics', 'farm automation', 'precision agriculture', 'smart farming', 'AI agriculture']
    },
    {
      id: 2,
      icon: <MdLocalShipping />,
      title: 'Agricultural Export Services',
      description: 'Global market access for premium agricultural produce',
      color: primaryOrange,
      gradient: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
      features: [
        'International quality certification',
        'Global logistics coordination',
        'Market linkage facilitation',
        'Export documentation support',
        'Cold chain management'
      ],
      benefits: [
        'Better price realization',
        'Access to premium international markets',
        'Reduced middlemen costs',
        'International standards compliance',
        'Supply chain optimization'
      ],
      keywords: ['agricultural exports', 'farm produce export', 'global market access', 'export services', 'international trade']
    },
    {
      id: 3,
      icon: <MdScience />,
      title: 'Fertilizer Manufacturing',
      description: 'Organic & bio-fertilizers for sustainable farming practices',
      color: primaryBlue,
      gradient: 'linear-gradient(135deg, #0288D1 0%, #01579B 100%)',
      features: [
        'Organic fertilizer production',
        'Bio-fertilizer manufacturing',
        'Custom NPK formulations',
        'Nutrient-rich soil solutions',
        'Sustainable fertilizer blends'
      ],
      benefits: [
        'Improved soil health',
        'Higher crop yield quality',
        'Reduced chemical dependency',
        'Sustainable farming practices',
        'Environmental protection'
      ],
      keywords: ['organic fertilizer', 'bio-fertilizer', 'sustainable farming', 'soil health', 'fertilizer production']
    },
    {
      id: 4,
      icon: <MdSchool />,
      title: 'Agricultural Consultancy',
      description: 'Expert guidance for profitable and sustainable farming',
      color: primaryPurple,
      gradient: 'linear-gradient(135deg, #7B1FA2 0%, #4A148C 100%)',
      features: [
        'Comprehensive farm planning',
        'Crop selection strategy',
        'Technology implementation',
        'Market access planning',
        'Financial optimization'
      ],
      benefits: [
        'Optimized farm operations',
        'Increased profitability',
        'Risk mitigation strategies',
        'Technology adoption support',
        'Sustainable growth planning'
      ],
      keywords: ['agricultural consultancy', 'farm consulting', 'agri business advisory', 'farm planning', 'agricultural experts']
    },
    {
      id: 5,
      icon: <MdLocalGroceryStore />,
      title: 'Organic Farm Products',
      description: 'Chemical-free produce from sustainable farms to consumers',
      color: '#388E3C',
      gradient: 'linear-gradient(135deg, #388E3C 0%, #1B5E20 100%)',
      features: [
        'Direct farm-to-table sourcing',
        'Quality testing laboratories',
        'Cold chain logistics',
        'Retail distribution network',
        'Organic certification'
      ],
      benefits: [
        'Premium market pricing',
        'Consumer health benefits',
        'Environmental sustainability',
        'Farm-to-consumer transparency',
        'Quality assurance'
      ],
      keywords: ['organic products', 'chemical-free produce', 'sustainable farming', 'farm fresh', 'organic certification']
    },
    {
      id: 6,
      icon: <MdLocalCafe />,
      title: 'Dairy & Milk Products',
      description: 'Fresh, hygienic dairy products from trusted sources',
      color: primaryRed,
      gradient: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
      features: [
        'Farm fresh milk collection',
        'Traditional processing methods',
        'Quality assurance protocols',
        'Home delivery services',
        'Dairy product variety'
      ],
      benefits: [
        'Pure & hygienic products',
        'Daily freshness guarantee',
        'Fair farmer pricing',
        'Direct consumer access',
        'Nutritional quality'
      ],
      keywords: ['dairy products', 'fresh milk', 'milk processing', 'dairy farming', 'milk products']
    }
  ];

  const stats = [
    { number: '6+', label: 'Core Services', icon: <FaTrophy />, color: primaryGreen },
    { number: '500+', label: 'Happy Farmers', icon: <MdPeople />, color: primaryBlue },
    { number: '1000+', label: 'Acres Transformed', icon: <GiWheat />, color: primaryOrange },
    { number: '40%', label: 'Average Yield Boost', icon: <FaSeedling />, color: primaryPurple },
    { number: '24/7', label: 'Support Available', icon: <MdAccessTime />, color: primaryRed },
    { number: '50+', label: 'Export Markets', icon: <FaGlobe />, color: '#388E3C' }
  ];

  const toggleService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <div className="services-page">
      {/* Enhanced Hero Section with SEO Focus */}
      <section className="services-hero" aria-label="Agricultural Services Overview">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <MdOutlineAgriculture />
                <span>Premium Agricultural Services</span>
              </div>
              
              <h1 className="hero-title">
                Comprehensive
                <span className="highlight-text"> Farming Solutions</span>
                for Modern Agriculture
              </h1>
              
              <p className="hero-description">
                We provide end-to-end agricultural solutions combining cutting-edge technology 
                with expert knowledge. Our comprehensive services are designed to maximize 
                productivity, profitability, and sustainability in modern farming.
              </p>
              
              {/* Enhanced Stats Grid */}
              <div className="hero-stats-grid">
                {stats.map((stat, index) => (
                  <div key={index} className="stat-card">
                    <div className="stat-icon-wrapper" style={{ background: `${stat.color}15` }}>
                      {stat.icon}
                    </div>
                    <div className="stat-content">
                      <div className="stat-number" style={{ color: stat.color }}>{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* CTA Buttons */}
              <div className="hero-actions">
                <button className="btn-primary">
                  <span>Get Expert Consultation</span>
                  <MdArrowForward />
                </button>
                <button className="btn-secondary">
                  <MdPlayCircle />
                  <span>View Service Demo</span>
                </button>
              </div>
            </div>
            
            {/* Hero Visual */}
            <div className="hero-visual">
              <div className="hero-image-card">
                <div className="card-icon">
                  <GiPlantWatering />
                </div>
                <div className="card-content">
                  <h3>All-in-One Agricultural Platform</h3>
                  <p>Technology + Expertise + Market Access + Sustainability</p>
                </div>
                <div className="card-features">
                  <span className="feature-tag" style={{ background: `${primaryGreen}15`, color: primaryGreen }}>
                    <MdSmartToy /> Smart Technology
                  </span>
                  <span className="feature-tag" style={{ background: `${primaryBlue}15`, color: primaryBlue }}>
                    <MdBarChart /> Expert Analysis
                  </span>
                  <span className="feature-tag" style={{ background: `${primaryOrange}15`, color: primaryOrange }}>
                    <FaGlobe /> Global Reach
                  </span>
                  <span className="feature-tag" style={{ background: `${primaryPurple}15`, color: primaryPurple }}>
                    <MdEco /> Sustainable
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="services-grid-section" aria-label="Our Agricultural Services">
        <div className="container">
          <header className="section-header">
            <span className="section-label">OUR AGRICULTURAL SERVICES</span>
            <h2>Comprehensive Farming Solutions</h2>
            <p className="section-subtitle">
              Explore our wide range of agricultural services designed to transform traditional 
              farming into modern, profitable, and sustainable agricultural practices.
            </p>
          </header>

   <div className="services-container">
  {services.map((service) => (
    <article
      key={service.id}
      className={`service-card ${
        expandedService === service.id ? "expanded" : ""
      }`}
    >
      <button
        type="button"
        className="service-card-main"
        onClick={() => toggleService(service.id)}
      >
        <div
          className="service-icon-wrapper"
          style={{ background: service.gradient }}
        >
          <div className="service-icon">
            {service.icon}
          </div>
        </div>

        <div className="service-content">
          <h3>{service.title}</h3>
          <p>{service.description}</p>

          <div className="service-keywords">
            {service.keywords.slice(0, 3).map((keyword, idx) => (
              <span key={idx} className="keyword-tag">
                {keyword}
              </span>
            ))}
          </div>
        </div>

        <div className="expand-btn">
          {expandedService === service.id ? (
            <MdExpandLess />
          ) : (
            <MdExpandMore />
          )}
        </div>
      </button>

      {expandedService === service.id && (
        <div className="service-details">
          <div className="details-grid">
            <div className="features-section">
              <h4>
                <MdWork className="list-title-icon" /> Key Features
              </h4>
              <ul className="features-list">
                {service.features.map((feature, idx) => (
                  <li key={idx}>
                    <MdCheckCircle className="check-icon" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="benefits-section">
              <h4>
                <MdVerified className="list-title-icon" /> Benefits
              </h4>
              <ul className="benefits-list">
                {service.benefits.map((benefit, idx) => (
                  <li key={idx}>
                    <MdVerified className="verified-icon" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="service-cta-inner">
            <button
              className="btn-primary"
              style={{ background: service.gradient }}
            >
              Enquire About This Service
              <MdArrowRight />
            </button>
          </div>
        </div>
      )}
    </article>
  ))}
</div>


        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section" aria-label="Why Choose Our Services">
        <div className="container">
          <div className="section-header">
            <span className="section-label">WHY CHOOSE US</span>
            <h2>Your Trusted Agricultural Partner</h2>
            <p className="section-subtitle">
              We combine technology, expertise, and market access to deliver exceptional value.
            </p>
          </div>
          
          <div className="advantages-grid">
            <div className="advantage-card">
              <div className="advantage-icon" style={{ background: `${primaryGreen}15` }}>
                <MdSecurity />
              </div>
              <h3>Proven Expertise</h3>
              <p>Years of experience in agricultural technology and farm management.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon" style={{ background: `${primaryBlue}15` }}>
                <MdStar />
              </div>
              <h3>Quality Assurance</h3>
              <p>International standards compliance and quality certifications.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon" style={{ background: `${primaryOrange}15` }}>
                <FaLeaf />
              </div>
              <h3>Sustainable Practices</h3>
              <p>Eco-friendly solutions for long-term agricultural sustainability.</p>
            </div>
            
            <div className="advantage-card">
              <div className="advantage-icon" style={{ background: `${primaryPurple}15` }}>
                <FaAppleAlt />
              </div>
              <h3>End-to-End Support</h3>
              <p>Complete support from planning to market access and beyond.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="services-cta" aria-label="Get Agricultural Consultation">
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2>Ready to Transform Your Agricultural Operations?</h2>
              <p>
                Schedule a free consultation with our agricultural experts. We'll analyze your 
                needs and provide customized solutions for maximum productivity and profitability.
              </p>
            </div>
            <div className="cta-action">
              <button className="btn-primary">Book Free Agricultural Consultation</button>
              <button className="btn-outline">Call Our Experts: +91 8010825179</button>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Using EXACT same color variables from Home page */
        :root {
          /* Header Fix Variables */
          --header-height-desktop: 80px;
          --header-height-mobile: 60px;
          
          /* Primary Colors - Same as Home page */
          --primary-green: #2E7D32;
          --primary-blue: #0288D1;
          --primary-orange: #F57C00;
          --primary-purple: #7B1FA2;
          --primary-red: #D32F2F;
          
          /* Neutral Colors - Same as Home page */
          --light-100: #F5F7FA;
          --light-200: #E8EAF6;
          --white: #FFFFFF;
          
          --text-primary: #121212;
          --text-secondary: #424242;
          
          --bg-primary: var(--white);
          --bg-secondary: var(--light-100);
          
          --border-color: rgba(0, 0, 0, 0.1);
          
          /* Effects */
          --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.05);
          --shadow-md: 0 4px 20px rgba(0, 0, 0, 0.1);
          --shadow-lg: 0 8px 40px rgba(0, 0, 0, 0.15);
          
          /* Border Radius */
          --radius-sm: 8px;
          --radius-md: 12px;
          --radius-lg: 16px;
          --radius-round: 50px;
          
          /* Transitions */
          --transition-normal: 0.3s ease;
          
          /* Font Family */
          --font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        /* Import Fonts */
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

        /* Base Styles */
        .services-page {
          font-family: var(--font-family);
          color: var(--text-primary);
          background: var(--bg-primary);
          padding-top: var(--header-height-desktop);
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        /* Enhanced Hero Section */
        .services-hero {
          padding: 100px 0 80px;
          position: relative;
          background: var(--bg-secondary);
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: -1;
        }

        .hero-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%);
        }

        .hero-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.03;
          background-image: radial-gradient(var(--primary-green) 1.5px, transparent 1.5px);
          background-size: 25px 25px;
        }

        .hero-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .hero-text {
          padding-right: 20px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(46, 125, 50, 0.1);
          color: var(--primary-green);
          padding: 10px 20px;
          border-radius: var(--radius-round);
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 30px;
          border: 1px solid rgba(46, 125, 50, 0.2);
        }

        .hero-title {
          font-family: 'Poppins', sans-serif;
          font-size: 52px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 20px;
          color: var(--text-primary);
        }

        .highlight-text {
          color: var(--primary-green);
          display: inline;
        }

        .hero-description {
          font-size: 18px;
          color: var(--text-secondary);
          margin-bottom: 40px;
          line-height: 1.7;
        }

        /* Enhanced Stats Grid */
        .hero-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          margin-bottom: 40px;
        }

        .stat-card {
          background: var(--white);
          border-radius: var(--radius-md);
          padding: 15px;
          border: 1px solid var(--border-color);
          box-shadow: var(--shadow-sm);
          transition: var(--transition-normal);
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: var(--shadow-md);
        }

        .stat-icon-wrapper {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 12px;
        }

        .stat-content {
          flex: 1;
        }

        .stat-number {
          font-family: 'Poppins', sans-serif;
          font-size: 24px;
          font-weight: 700;
          line-height: 1;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 13px;
          color: var(--text-secondary);
          font-weight: 500;
        }

        /* Hero Actions */
        .hero-actions {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }

        .btn-primary, .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 28px;
          border-radius: var(--radius-round);
          font-size: 16px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: var(--transition-normal);
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--primary-green) 0%, #388E3C 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(46, 125, 50, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(46, 125, 50, 0.4);
        }

        .btn-secondary {
          background: var(--white);
          color: var(--primary-green);
          border: 2px solid var(--primary-green);
        }

        .btn-secondary:hover {
          background: rgba(46, 125, 50, 0.05);
          transform: translateY(-2px);
        }

        /* Hero Visual */
        .hero-visual {
          display: flex;
          justify-content: center;
        }

        .hero-image-card {
          background: var(--white);
          border-radius: var(--radius-lg);
          padding: 30px;
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--border-color);
          text-align: center;
          max-width: 400px;
          width: 100%;
        }

        .card-icon {
          font-size: 48px;
          color: var(--primary-green);
          margin-bottom: 20px;
        }

        .card-content h3 {
          font-family: 'Poppins', sans-serif;
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 10px;
          color: var(--text-primary);
        }

        .card-content p {
          color: var(--text-secondary);
          font-size: 16px;
          margin-bottom: 25px;
        }

        .card-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .feature-tag {
          padding: 8px 12px;
          border-radius: var(--radius-sm);
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 5px;
          justify-content: center;
        }

        /* Services Grid Section */
        .services-grid-section {
          padding: 100px 0;
          background: var(--bg-primary);
        }

        .section-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 60px;
        }

        .section-label {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(46, 125, 50, 0.1);
          color: var(--primary-green);
          border-radius: var(--radius-round);
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 20px;
          border: 1px solid rgba(46, 125, 50, 0.2);
        }

        .section-header h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 42px;
          font-weight: 700;
          margin-bottom: 16px;
          color: var(--text-primary);
        }

        .section-subtitle {
          font-size: 18px;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .services-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .service-card {
          background: var(--white);
          border-radius: var(--radius-lg);
          overflow: hidden;
          border: 1px solid var(--border-color);
          cursor: pointer;
          transition: var(--transition-normal);
        }

        .service-card:hover {
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .service-card.expanded {
          border-color: var(--primary-green);
          box-shadow: var(--shadow-lg);
        }

        .service-card-main {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 25px;
          transition: var(--transition-normal);
          position: relative;
        }

        .service-icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          flex-shrink: 0;
        }

        .service-content {
          flex: 1;
        }

        .service-content h3 {
          font-family: 'Poppins', sans-serif;
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 8px;
          color: var(--text-primary);
        }

        .service-content p {
          color: var(--text-secondary);
          font-size: 15px;
          margin-bottom: 12px;
        }

        .service-keywords {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .keyword-tag {
          background: var(--bg-secondary);
          color: var(--text-secondary);
          padding: 4px 10px;
          border-radius: var(--radius-round);
          font-size: 12px;
          font-weight: 500;
        }

        .expand-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          transition: var(--transition-normal);
          flex-shrink: 0;
        }

        .service-card:hover .expand-btn,
        .service-card.expanded .expand-btn {
          background: var(--primary-green);
          color: white;
        }

        .service-details {
          padding: 30px;
          border-top: 1px solid var(--border-color);
          background: var(--bg-secondary);
        }

        .details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          margin-bottom: 30px;
        }

        .features-section h4,
        .benefits-section h4 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 20px;
          color: var(--text-primary);
        }

        .list-title-icon {
          font-size: 22px;
          color: var(--primary-green);
        }

        .features-list,
        .benefits-list {
          list-style: none;
          padding: 0;
        }

        .features-list li,
        .benefits-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 14px;
          color: var(--text-primary);
          font-size: 15px;
          line-height: 1.5;
        }

        .check-icon,
        .verified-icon {
          font-size: 18px;
          flex-shrink: 0;
          margin-top: 2px;
          color: var(--primary-green);
        }

        .service-cta-inner {
          text-align: right;
        }

        /* Why Choose Us Section */
        .why-choose-section {
          padding: 80px 0;
          background: var(--bg-secondary);
        }

        .advantages-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .advantage-card {
          text-align: center;
          padding: 30px 20px;
          background: var(--white);
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-color);
          transition: var(--transition-normal);
        }

        .advantage-card:hover {
          transform: translateY(-5px);
          box-shadow: var(--shadow-md);
        }

        .advantage-icon {
          width: 60px;
          height: 60px;
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          margin: 0 auto 20px;
        }

        .advantage-card h3 {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 10px;
          color: var(--text-primary);
        }

        .advantage-card p {
          color: var(--text-secondary);
          font-size: 15px;
          line-height: 1.6;
        }

        /* CTA Section */
        .services-cta {
          padding: 100px 0;
          background: linear-gradient(135deg, var(--primary-green) 0%, #1B5E20 100%);
          color: white;
          text-align: center;
        }

        .cta-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .cta-text h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 42px;
          font-weight: 700;
          margin-bottom: 20px;
          line-height: 1.1;
        }

        .cta-text p {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .cta-action {
          display: flex;
          gap: 15px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-outline {
          background: transparent;
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.5);
          padding: 14px 28px;
          border-radius: var(--radius-round);
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition-normal);
        }

        .btn-outline:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: white;
          transform: translateY(-2px);
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .advantages-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .hero-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 1024px) {
          .hero-content {
            grid-template-columns: 1fr;
            gap: 50px;
            text-align: center;
          }

          .hero-text {
            padding-right: 0;
          }

          .hero-actions {
            justify-content: center;
          }

          .hero-image-card {
            margin: 0 auto;
          }
        }

        @media (max-width: 768px) {
          .services-page {
            padding-top: var(--header-height-mobile);
          }

          .services-hero {
            padding: 80px 0 60px;
          }

          .hero-title {
            font-size: 36px;
          }

          .hero-description {
            font-size: 16px;
          }

          .hero-stats-grid {
            grid-template-columns: 1fr;
          }

          .hero-actions {
            flex-direction: column;
            align-items: center;
          }

          .btn-primary, .btn-secondary {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }

          .services-grid-section {
            padding: 80px 0;
          }

          .section-header h2 {
            font-size: 32px;
          }

          .details-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }

          .service-card-main {
            padding: 20px;
            flex-direction: column;
            text-align: center;
            gap: 15px;
          }

          .expand-btn {
            position: absolute;
            top: 20px;
            right: 20px;
          }

          .service-keywords {
            justify-content: center;
          }

          .advantages-grid {
            grid-template-columns: 1fr;
          }

          .cta-text h2 {
            font-size: 32px;
          }

          .cta-action {
            flex-direction: column;
            align-items: center;
          }

          .btn-primary, .btn-outline {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 16px;
          }

          .services-hero {
            padding: 60px 0 40px;
          }

          .hero-title {
            font-size: 28px;
          }

          .hero-badge {
            font-size: 13px;
            padding: 8px 16px;
          }

          .stat-card {
            padding: 12px;
          }

          .card-features {
            grid-template-columns: 1fr;
          }

          .service-content h3 {
            font-size: 20px;
          }

          .feature-tag {
            font-size: 12px;
            padding: 6px 10px;
          }

          .cta-text h2 {
            font-size: 28px;
          }

          .services-cta {
            padding: 80px 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Services;
